import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import Typography from "components/common/Typography";
import { uniqueId } from "lodash";
import * as React from "react";
import { useState } from "react";
import { tokens } from "styles/Tokens";
import { messages, minSearchStrLength } from "utils/CommonConstants";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";

interface Props {
  isLoading?: boolean;
  label: string;
  options: any;
  optionKeyName?: string;
  onSubmit: (args?: any) => void;
  isFiltered?: boolean;
  upperCase?: boolean;
  selectedOption: string;
  inputValue?: string;
  handleInputChange?: (...args: any) => void;
  placeholder?: string;
  showSelectedOption?: boolean;
  width?: string;
  showTextField?: boolean;
  noOptionsMessage?: string;
  selectedKeyOption?: string;
}

const SingleSelectFilter = (props: Props) => {
  const {
    isLoading = false,
    label,
    options,
    optionKeyName,
    onSubmit,
    isFiltered,
    upperCase = false,
    selectedOption,
    inputValue,
    handleInputChange,
    placeholder,
    showSelectedOption = false,
    width,
    showTextField = true,
    noOptionsMessage = messages.noOwners,
    selectedKeyOption = optionKeyName
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [selected, setSelected] = useState<string>("");

  const textFieldRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (textFieldRef.current && open) {
      textFieldRef.current.focus();
    }
  }, [inputValue, open]);

  const SelectStyles = {
    height: "44px",
    display: "flex",
    color: tokens.Text.General.PrimaryHeadline,
    ":hover": {
      backgroundColor: tokens.Background.General.Color,
      color: tokens.Text.General.Color
    },
    "&.MuiMenuItem-root.Mui-selected": {
      backgroundColor: tokens.Background.General.Color,
      color: tokens.Text.General.Color
    }
  };

  React.useEffect(() => {
    window.dispatchEvent(new CustomEvent("resize"));
  }, [open === true]);

  React.useEffect(() => {
    if (!isFiltered) {
      setSelected("");
    }
  }, [isFiltered]);

  React.useEffect(() => {
    let selected;
    if (Array.isArray(selectedOption)) selected = selectedOption[0];
    else selected = selectedOption;
    setSelected(selected);
  }, [selectedOption]);

  const handleOpenMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e: any, reason: any) => {
    setAnchorEl(null);
  };

  const handleMenuChange = (val: string) => {
    setSelected(() => {
      onSubmit(val);
      return val;
    });
    setAnchorEl(null);
  };

  const innerResult = selected?.length ? `(1)` : "";

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          backgroundColor: tokens.Background.General.Tertiary,
          height: "44px",
          paddingX: "8px",
          marginX: "4px",
          borderRadius: "8px",
          cursor: "pointer",
          pt: "2px",
          width: { width }
        }}
        onClick={handleOpenMenu}
      >
        <Typography
          variant="body2"
          showBold
          color={tokens.Text.General.Secondary}
          sx={{ flex: 1, whiteSpace: "nowrap", mr: "32px" }}
        >
          {showSelectedOption ? selectedOption || "undefined" : `${label} ${innerResult}`}
        </Typography>
        {open ? <KeyboardArrowUpIcon sx={{ fontSize: "22px" }} /> : <ArrowDown />}
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        PaperProps={{
          style: {
            maxHeight: "300px",
            maxWidth: "360px",
            borderRadius: "12px",
            minWidth: "240px"
          }
        }}
      >
        {showTextField && (
          <MenuItem sx={{ padding: "8px", pt: 0 }}>
            <TextField
              fullWidth
              placeholder={placeholder ?? "Search User"}
              sx={{ height: "44px" }}
              value={inputValue}
              onChange={(e) => {
                e.stopPropagation();
                handleInputChange?.(e);
              }}
              type="search"
              onKeyDown={(e) => e.stopPropagation()}
              inputRef={textFieldRef}
            />
          </MenuItem>
        )}
        {options?.length
          ? options?.map((opt: any) => {
              const isUser = optionKeyName === "displayName";
              const isSelected = selectedKeyOption ? selected === opt[selectedKeyOption] : selected === opt;
              return (
                opt &&
                opt !== undefined && (
                  <MenuItem
                    key={uniqueId("SINGLEOPTIONS_")}
                    sx={{ textTransform: upperCase ? "uppercase" : "none", ...SelectStyles }}
                    onClick={() => handleMenuChange(selectedKeyOption ? opt[selectedKeyOption] : opt)}
                    selected={isSelected}
                  >
                    <Stack>
                      <Typography variant="body2">
                        {isUser
                          ? getUserNameWithConditionalCheck(opt.displayName, opt.firstName, opt.lastName)
                          : optionKeyName
                          ? opt[optionKeyName]
                          : opt}
                      </Typography>{" "}
                      <Typography
                        variant="label"
                        color={isSelected ? tokens.Text.General.Color : tokens.Text.General.Secondary}
                      >
                        {opt?.email}
                      </Typography>
                    </Stack>
                  </MenuItem>
                )
              );
            })
          : !showTextField && (
              <Typography
                variant="label"
                color={tokens.Text.General.Secondary}
                sx={{ margin: "8px", display: "block", width: "100%" }}
              >
                {noOptionsMessage}
              </Typography>
            )}

        {inputValue && inputValue?.length > minSearchStrLength && (
          <Typography
            variant="label"
            color={tokens.Text.General.Secondary}
            sx={{ margin: "8px", display: "block", width: "100%" }}
          >
            {isLoading ? "Loading..." : !options?.length && messages.userNotFound}
          </Typography>
        )}
      </Menu>
    </>
  );
};

export default SingleSelectFilter;
